import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTranslate } from '@repo/unicity'
import { RECAPTCHA_V2_KEY, RECAPTCHA_V2_QA_KEY } from './recaptcha.constants'

const STATUS = {
    loading: 'loading',
    ready: 'ready',
    scriptFailed: 'script-failed',
    widgetFailed: 'widget-failed'
} as const

type Status = (typeof STATUS)[keyof typeof STATUS]

type GrecaptchaV2 = {
    ready: (callback: () => void) => void
    render: (
        container: HTMLElement,
        parameters: {
            sitekey: string
            hl: string
            callback: (token: string) => void
            'expired-callback': () => void
            'error-callback': () => void
        }
    ) => number
    reset: (widgetId?: number) => void
}

type ReCaptchaProps = {
    language: string
    onVerified: () => void
}

const getRecaptchaV2Key = () => {
    const liveDomains = ['ufg.expert']
    return liveDomains.includes(window.location.hostname) ? RECAPTCHA_V2_KEY : RECAPTCHA_V2_QA_KEY
}

const loadV2Script = (language: string) => {
    const existing = window.grecaptcha as unknown as GrecaptchaV2 | undefined
    if (existing?.render) return Promise.resolve(existing)

    return new Promise<GrecaptchaV2>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${language}`
        script.async = true
        script.defer = true
        script.onload = () => {
            const grecaptcha = window.grecaptcha as unknown as GrecaptchaV2 | undefined
            if (!grecaptcha?.render) {
                reject(new Error('recaptcha v2 unavailable'))
                return
            }
            grecaptcha.ready(() => resolve(grecaptcha))
        }
        script.onerror = reject
        document.head.appendChild(script)
    })
}

export const ReCaptchaV2 = ({ language, onVerified }: ReCaptchaProps) => {
    const { translate } = useTranslate()
    const [status, setStatus] = useState<Status>(STATUS.loading)
    const [attempt, setAttempt] = useState(0)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const widgetIdRef = useRef<number | undefined>(undefined)
    const siteKey = useMemo(getRecaptchaV2Key, [])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let cancelled = false
        container.innerHTML = ''
        setStatus(STATUS.loading)

        loadV2Script(language)
            .then(grecaptcha => {
                if (cancelled) return
                widgetIdRef.current = grecaptcha.render(container, {
                    sitekey: siteKey,
                    hl: language,
                    callback: token => {
                        if (!token) return
                        setStatus(STATUS.ready)
                        onVerified()
                    },
                    'expired-callback': () => grecaptcha.reset(widgetIdRef.current),
                    'error-callback': () => setStatus(STATUS.widgetFailed)
                })
                setStatus(STATUS.ready)
            })
            .catch(() => {
                if (!cancelled) setStatus(STATUS.scriptFailed)
            })

        return () => {
            cancelled = true
            container.innerHTML = ''
            widgetIdRef.current = undefined
        }
    }, [attempt, language, onVerified, siteKey])

    const retryWidget = () => {
        setAttempt(current => current + 1)
    }

    if (status === STATUS.scriptFailed || status === STATUS.widgetFailed) {
        return (
            <Alert
                severity="error"
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={status === STATUS.scriptFailed ? () => window.location.reload() : retryWidget}
                        data-testid="recaptcha-v2-retry-button">
                        {translate('retry')}
                    </Button>
                }>
                {translate('recaptcha_unavailable')}
            </Alert>
        )
    }

    return (
        <Stack spacing={1} alignItems="center">
            <Box ref={containerRef} sx={{ display: status === STATUS.ready ? 'block' : 'none' }} data-testid="recaptcha-v2-widget" />
            {status === STATUS.loading && <Skeleton variant="rounded" width="304px" height="78px" sx={{ borderRadius: '12px' }} />}
        </Stack>
    )
}
